import {ReactNode, useEffect} from 'react';
import {Navigate} from 'react-router-dom';
import {useNavigationGuard} from '../utils/GlobalNavigationGuard';
import UnsavedChangesPopup from '../components/productions/unsaved-changes-popup/UnsavedChangesPopup';
import {routePermissions} from '../config/routePermissions';
import {store} from '../redux/store';
interface PrivateRouteProps {
  children: ReactNode;
  isAuthenticated: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  isAuthenticated,
}) => {
  const {
    blockNavigation,
    modalVisible: shouldDisplayProductionUnsavedPopup,
    confirmNavigation,
    cancelNavigation,
  } = useNavigationGuard();

  // Intercept navigation  for productions module
  useEffect(() => {
    const unblock = window.history.pushState;
    window.history.pushState = function (...args) {
      const path = args[2] as string;
      if (blockNavigation(path) === false) return;
      return unblock.apply(this, args);
    };
    return () => {
      window.history.pushState = unblock;
    };
  }, [blockNavigation]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const normalizedPermissions = Object.entries(routePermissions).map(
    ([route, roles]) => ({
      route: route.toLowerCase(),
      roles,
    })
  );

  const matchedRoute = normalizedPermissions
    .filter(({route}) => location.pathname.toLowerCase().startsWith(route))
    .sort((a, b) => b.route.length - a.route.length)[0];

  const allowedRoles = matchedRoute?.roles;
  const userRole = store.getState().authenticatedUser.user?.roleId;
  const user = store.getState().authenticatedUser?.user;
  const userRights = store.getState().actionmenu?.userRights;

  const restrictForProjectAdministrator = ['/assignment', '/codingform'];

  if (
    user?.roleName == 'Project Administrator' &&
    restrictForProjectAdministrator.includes(matchedRoute?.route)
  ) {
    return <Navigate to="/AccessDenied" replace />;
  }

  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    return <Navigate to="/AccessDenied" replace />;
  }
  const path = location.pathname.toLowerCase();
  if (
    path.startsWith('/ai/investigate') &&
    (!userRights?.canAIInvestigate ||
      (path.includes('manage-indices') && !userRights?.canManageIndices))
  ) {
    return <Navigate to="/AccessDenied" replace />;
  }
  if (
    path.startsWith('/ai/summarize') &&
    (!userRights?.canAIBatchSummarize)
  ) {
    return <Navigate to="/AccessDenied" replace />;
  }

  return (
    <>
      {children}

      {shouldDisplayProductionUnsavedPopup && (
        <UnsavedChangesPopup
          showModal={shouldDisplayProductionUnsavedPopup}
          onClose={() => {
            cancelNavigation();
          }}
          onSave={() => {
            confirmNavigation();
          }}
        ></UnsavedChangesPopup>
      )}
    </>
  );
};

export default PrivateRoute;
